// noinspection SpellCheckingInspection

import {BaseBrandedView, BaseBrandedViewOptions} from "./_BaseViews";
import * as _ from "lodash";
import {checkPasswordPolicy, PasswordPolicyItem} from "../common/passwords";

declare const CONFIG;
declare const grecaptcha;

const emailInputSelector = '#forgotpw-input-email';
const passwordInputSelector = '#pwreset-input-password';
const passwordConfirmInputSelector = '#pwreset-input-password2';
const forgotPasswordFormId = 'forgotpw-form';
const forgotPasswordSubmitButtonSelector = '#forgotpw-submit-btn';

interface ForgotPasswordViewOptions extends BaseBrandedViewOptions {
    userId: number;
    token: string;
}

export class ForgotPasswordView extends BaseBrandedView {
    options: ForgotPasswordViewOptions;

    private doneForgotPW = false;
    private doneReset = false;
    private recaptchaSiteKey = CONFIG.recaptcha.v3.siteKey;
    private recaptchaToken = '';
    private template = _.template(require('templates/forgotPassword.html'));
    private userMessage = '';
    private _isProcessing = false;
    private _isValidEmail = false;

    get isProcessing() {
        return this._isProcessing;
    }
    set isProcessing(val) {
        if (val === this._isProcessing) return;
        this._isProcessing = val;
        this.updateButtonEnabled();
    }

    get isValidEmail() {
        return this._isValidEmail;
    }
    set isValidEmail(val) {
        if (val === this._isValidEmail) return;
        this._isValidEmail = val;
        this.updateButtonEnabled();
    }

    get enableForgotPasswordBtn() {
        return this.isValidEmail && !this.isProcessing;
    }

    constructor(options: ForgotPasswordViewOptions) {
        super(<any> _.extend(options, {
            tagName: 'div',
            events: {
                "submit #pwreset-form": "handleResetPWSubmit",
                'input #pwreset-input-password': 'handlePasswordInput',
                'input #pwreset-input-password2': 'handlePasswordInput',
                'input #forgotpw-input-email': 'handleEmailInput',
                'click #forgotpw-submit-btn': 'handleForgotPasswordClick',
            }
        }));

        if(options.userId){
            this.doneForgotPW = true;
            this.doneReset = false;
        }else{
            this.doneForgotPW = false;
            this.doneReset = false;
        }
    };

    private getEmail() {
        return this.$(emailInputSelector).val().toString().trim().toLowerCase();
    }

    private handleEmailInput() {
        const email = this.getEmail();
        this.isValidEmail = (typeof email === 'string') && email.length > 0;
    }

    private handleForgotPasswordClick(e) {
        // If the reCAPTCHA library has not loaded, do not attempt to enrich
        // the form submission with the reCAPTCHA token. The server will
        // reject the request.
        if (grecaptcha === null || grecaptcha === undefined || typeof grecaptcha.ready !== 'function') {
            console.warn('grecaptcha not configured');
            return;
        }

        // Prevent form submitting -- we will trigger the form's submit
        // programmatically below.
        e.preventDefault();

        // Disable the forgot password submit button
        this.isProcessing = true;

        // When the reCAPTCHA library is ready, run the reCAPTCHA evaluation
        const self = this;
        grecaptcha.ready(function captchaReady() {
            grecaptcha.execute(self.recaptchaSiteKey, {action: 'forgot_password'}).then(function captchaToken(token) {
                // Save the resulting token in an instance variable to use in
                // the AJAX request when the form is submitted.
                self.recaptchaToken = token;

                // Looking up the <form> element in the DOM and calling
                // form.submit() programmatically does not trigger the 'submit'
                // DOM event, so instead we directly call the function that
                // used to registered as the 'submit' event handler.
                self.handleForgotPWSubmit(e);
            });
        });

        this.render();
    }

    private handleForgotPWSubmit(e) {
        // Prevent form submit
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }

        const email = this.getEmail();
        const brand = this.options.brandCollection.getBrand();
        const recaptchaToken = this.recaptchaToken;

        $.ajax({
            type: "POST",
            url: window.location.origin + '/forgot-password',
            contentType: 'application/x-www-form-urlencoded',
            dataType: 'json',
            data: {
                'brandId': brand.get('id'),
                'email': email,
                'recaptchaToken': recaptchaToken,
            },
            success: (data) => {
                this.isProcessing = false;
                this.doneForgotPW = true;

                this.userMessage = 'Your request is being processed by our servers.';
                if (data !== null && typeof data === 'object' && typeof data.message === 'string') {
                    this.userMessage = data.message;
                }

                this.render();
            },
            error: (jqXHR) => {
                this.isProcessing = false;
                this.doneForgotPW = false;

                let errors = ['Unable to process your request.'];
                if (jqXHR && jqXHR.responseJSON && typeof jqXHR.responseJSON.message === 'string') {
                    errors = [jqXHR.responseJSON.message];
                }
                this.render(errors);
            }
        });
    }

    private handlePasswordInput() {
        var password = this.$(passwordInputSelector).val().toString();
        var policyCheck = checkPasswordPolicy(password);
        _.each(policyCheck.pass, (p) => {
            this.$(`.password-policy-item[data-item="${p}"]`).removeClass('fa-times text-danger-bright');
            this.$(`.password-policy-item[data-item="${p}"]`).addClass('fa-check text-success-bright');
        });
        _.each(policyCheck.fail, (p) => {
            this.$(`.password-policy-item[data-item="${p}"]`).addClass('fa-times text-danger-bright');
            this.$(`.password-policy-item[data-item="${p}"]`).removeClass('fa-check text-success-bright');
        });

        var passwordConfirm = this.$(passwordConfirmInputSelector).val().toString();
        this.$(`.password-policy-match-info`).removeClass('hidden');
        this.$(`.password-policy-match`).toggleClass('fa-times text-danger-bright', password != passwordConfirm);
        this.$(`.password-policy-match`).toggleClass('fa-check text-success-bright', password == passwordConfirm);

        this.$(`button`).prop('disabled', policyCheck.fail.length || password != passwordConfirm);
    }

    private handleResetPWSubmit(e) {
        // Prevent form submit
        e.preventDefault();
        var password = this.$(passwordInputSelector).val();
        $.ajax({
            type: "POST",
            url: window.location.origin + '/reset-password',
            contentType: 'application/x-www-form-urlencoded',
            dataType: 'json',
            data: {
                password: password,
                userId: this.options.userId,
                token: this.options.token,
            },
            success: (data) => {
                this.isProcessing = false;
                this.doneReset = true;

                this.userMessage = 'Your request is being processed by our servers.';
                if (data !== null && typeof data === 'object' && typeof data.message === 'string') {
                    this.userMessage = data.message;
                }

                this.render();
            },
            error: (jqXHR) => {
                this.isProcessing = false;
                this.doneReset = false;

                let errors = ['Unable to process your request.'];
                if (jqXHR && jqXHR.responseJSON && typeof jqXHR.responseJSON.message === 'string') {
                    errors = [jqXHR.responseJSON.message];
                }
                this.render(errors);
            }
        });
    };

    render(errors?) {
        const err = errors ?? [];

        const viewModel = {
            doneForgotPW: this.doneForgotPW,
            doneReset: this.doneReset,
            enableForgotPasswordBtn: this.enableForgotPasswordBtn,
            email: this.$(emailInputSelector).val(),
            errors: err,
            password: this.$(passwordInputSelector).val(),
            password2: this.$(passwordConfirmInputSelector).val(),
            PasswordPolicyItem: PasswordPolicyItem,
            recaptchaSiteKey: this.recaptchaSiteKey,
            token: this.options.token,
            userId: this.options.userId,
            userMessage: this.userMessage,
        };

        this.$el.html(this.template(viewModel));

        return this;
    }

    updateButtonEnabled() {
        const disabled = !this.enableForgotPasswordBtn;
        this.$el.find(forgotPasswordSubmitButtonSelector).prop('disabled', disabled);
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/ForgotPasswordView.ts